import { useState } from "react";
import { useDispatch, useStore } from "react-redux";
import * as Sentry from "@sentry/react";
import type { AppDispatch, RootState } from "@/store/store";
import userService from "@/endpoints/userService";
import ShowToaster from "@/toaster";
import { setPendingSelectionIfChanged } from "@/store/slices/pendingSelection/pendingSelectionSlice";
import { fetchSegments, setSegmentApplied } from "@/store/slices/segments/segments.thunk";
import { MAX_APPLIED_SEGMENTS } from "@/components/projectCommon/Filters/reducer/appliedSegmentsReducer";
import { selectAppliedSegmentCodes } from "@/store/slices/segments/selectors";
import { adoptBrandSetIntoStore } from "@/components/BrandSets/shared/adoptBrandSet";
import type { BrandSetView } from "@/components/BrandSets/shared/brandSet.types";
import type { CONFIG_SCHEMA } from "@/store/slices/screenConfig/screenConfigSlice";
import type { SelectionsRefOrigin } from "@/utils/selections/buildValidateRequest";
import type {
  SelectionsApplyResponse,
  SelectionsErrorBody,
} from "@/interfaces/selections/selections.types";

const errorMessageForCode = (code?: string): string => {
  if (code === "FEATURE_DISABLED") return "Brand Sets isn't enabled for your account.";
  // A 409 means the validate result went stale, so retrying returns 409 forever.
  if (code === "APPLY_CONFLICT") {
    return "Some items are no longer available. Close and reopen the link to see what changed.";
  }
  return "We couldn't add these to your account. Please try again.";
};

async function readErrorBody(err: unknown): Promise<SelectionsErrorBody | null> {
  const response = (err as { response?: { json?: () => Promise<unknown> } })?.response;
  if (typeof response?.json !== "function") return null;
  try {
    return (await response.json()) as SelectionsErrorBody;
  } catch {
    return null;
  }
}

export function useAdoptSelections(): {
  adopt: (
    brandSetUlids: string[],
    segmentCodes: string[],
    origin: SelectionsRefOrigin,
  ) => Promise<boolean>;
  isApplying: boolean;
  errorMessage: string | null;
} {
  const dispatch = useDispatch<AppDispatch>();
  const store = useStore<RootState>();
  const [isApplying, setIsApplying] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Hydrates the store with what `apply` already wrote server-side, and
  // selects it in the current view. The write already happened by the time
  // this runs, so a throw here is a partial success, not a failed add.
  // Returns the number of adopted segments the applied-set cap refused.
  const hydrateAdopted = async (
    response: SelectionsApplyResponse,
    origin: SelectionsRefOrigin,
  ): Promise<number> => {
    if (response.brandSets?.length) {
      const raw = await userService.brandSets.list();
      const list: BrandSetView[] = Array.isArray(raw) ? raw : (raw?.data ?? []);
      const toBrands: string[] = [];
      const toSecondary: string[] = [];
      for (const mapping of response.brandSets) {
        // Re-read state each iteration: adoptBrandSetIntoStore's
        // setBrandsMappingForMarket dispatch replaces the whole per-market
        // order, so a stale pre-loop snapshot would make each call clobber
        // the previous iteration's write on a multi-set batch.
        const freshState = store.getState();
        const view = list.find((v) => v.ulid === mapping.result);
        if (view) {
          adoptBrandSetIntoStore(view, {
            dispatch,
            brandsOrderMapping: freshState.activeGroup.brandsOrderMapping,
            brandEntities: freshState.activeGroup.brands.entities,
          });
        }
        const bucket =
          origin.brandSetOrigin[mapping.source] === "secondaryBrands" ? toSecondary : toBrands;
        if (!bucket.includes(mapping.result)) bucket.push(mapping.result);
      }
      const current = store.getState().userSelection;
      dispatch(
        setPendingSelectionIfChanged({
          brands: Array.from(new Set([...(current.brands ?? []), ...toBrands])),
          secondaryBrands: Array.from(new Set([...(current.secondaryBrands ?? []), ...toSecondary])),
        }),
      );
    }

    if (!response.segments?.length) return 0;

    // Hydrate the cloned segment codes into the catalog before applying them
    // (setSegmentApplied rejects unknown codes; the listener prunes codes not in the pool).
    // .unwrap() so a catalog-fetch failure throws into phase 2's catch instead
    // of silently leaving hydrateAdopted to run against a stale/empty catalog
    // (every subsequent setSegmentApplied would then reject with
    // SEGMENT_NOT_FOUND — the one rejection branch that raises no toast).
    await dispatch(fetchSegments()).unwrap();
    // screenConfig's slice-level type is `CONFIG_SCHEMA | {}` (empty before any
    // route registers its config), so RootState narrows the field access away —
    // cast through CONFIG_SCHEMA as the rest of the codebase does (appliedSegmentsMirror.ts).
    const mode = (store.getState().screenConfig as CONFIG_SCHEMA | undefined)
      ?.segmentFilterModalMode;
    const resultCodes = response.segments.map((m) => m.result);

    if (mode !== "SEGMENT_ONLY") {
      const current = store.getState().userSelection.segments ?? [];
      dispatch(
        setPendingSelectionIfChanged({
          segments: Array.from(new Set([...current, ...resultCodes])),
        }),
      );
      return 0;
    }

    // setSegmentApplied does not enforce the cap, so clamp here rather than
    // persisting an applied set every other entry point refuses to create.
    // Read the count after fetchSegments so the catalog is current.
    //
    // `response.segments` can legitimately include ALREADY_HAVE/OWNED/ACCESSIBLE
    // codes the caller already has applied (not just CREATED), so it can
    // overlap with the already-applied set. Filter that overlap out before
    // computing headroom — otherwise a re-apply of an already-applied code
    // eats a "slot" that was never actually needed, understating headroom
    // and either starving a genuinely new code of a free slot or reporting a
    // refusal when nothing was refused.
    const applied = new Set(selectAppliedSegmentCodes(store.getState()));
    const pending = resultCodes.filter((code) => !applied.has(code));
    const headroom = Math.max(0, MAX_APPLIED_SEGMENTS - applied.size);
    const toApply = pending.slice(0, headroom);
    toApply.forEach((code) => dispatch(setSegmentApplied({ code, isApplied: true })));
    return pending.length - toApply.length;
  };

  const adopt = async (
    brandSetUlids: string[],
    segmentCodes: string[],
    origin: SelectionsRefOrigin,
  ): Promise<boolean> => {
    setIsApplying(true);
    setErrorMessage(null);

    // Phase 1 — the write. Only a failure here means nothing was added.
    let response: SelectionsApplyResponse;
    try {
      response = await userService.selections.apply({
        ...(brandSetUlids.length ? { brandSets: brandSetUlids } : {}),
        ...(segmentCodes.length ? { segments: segmentCodes } : {}),
      });
    } catch (err) {
      const body = await readErrorBody(err);
      const code = body?.code;
      // Expected user-facing outcomes don't need Sentry noise; capture everything else.
      if (code !== "FEATURE_DISABLED" && code !== "APPLY_CONFLICT") {
        Sentry.captureException(err, { tags: { feature: "selections", op: "apply" } });
      }
      setErrorMessage(errorMessageForCode(code));
      setIsApplying(false);
      return false;
    }

    // Phase 2 — hydrate and select. The clone already exists server-side, so a
    // failure here is a partial success: report it honestly and still close.
    try {
      const cappedSegments = await hydrateAdopted(response, origin);
      if (cappedSegments > 0) {
        ShowToaster.warning(
          `Added to your account. ${cappedSegments} ${
            cappedSegments === 1 ? "segment wasn't" : "segments weren't"
          } applied — you can apply up to ${MAX_APPLIED_SEGMENTS} at a time.`,
        );
      } else {
        ShowToaster.success("Added to your account.");
      }
    } catch (err) {
      Sentry.captureException(err, { tags: { feature: "selections", op: "apply-hydrate" } });
      ShowToaster.warning(
        "Added to your account, but we couldn't apply them to this view. Reload to use them.",
      );
    } finally {
      setIsApplying(false);
    }
    return true;
  };

  return { adopt, isApplying, errorMessage };
}
